import React, { useState, useMemo, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, ToggleButtonGroup, ToggleButton, TableFooter, useTheme, useMediaQuery } from '@mui/material';
import { useTranslate, useNotify } from 'react-admin';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TableChart, ShowChart } from '@mui/icons-material';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { useReportData } from '../../../hooks/useReportData';
import { useReportExport } from '../../../hooks/useReportExport';
import { formatCurrency, formatNumber, formatDate, getDateRangePresets } from '../../../utils/reportHelpers';
import { API_CONFIG } from '../../../config/api';

interface TransactionSummary {
  total_count: number;
  total_amount: number;
  completed_count: number;
  pending_count: number;
  failed_count: number;
  success_rate: number;
  avg_transaction_amount: number;
}

interface TimeSeriesData {
  date: string;
  count: number;
  amount: number;
  completed: number;
  success_rate: number;
}

interface TypeBreakdown {
  type: string;
  count: number;
  amount: number;
}

interface MethodBreakdown {
  method: string;
  count: number;
  amount: number;
}

interface StatusBreakdown {
  status: string;
  count: number;
  amount: number;
}

interface TransactionReportData {
  summary: TransactionSummary;
  time_series: TimeSeriesData[];
  breakdown_by_type: TypeBreakdown[];
  breakdown_by_method: MethodBreakdown[];
  breakdown_by_status: StatusBreakdown[];
}

const COLORS = ['#009A75', '#2196F3', '#FFB800', '#FF6B6B', '#9C27B0', '#00BCD4'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const tableCellWrapSx = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  verticalAlign: 'top',
};

const tableDetailsSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    ...tableCellWrapSx,
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
};

const mobileRowCardSx = {
  p: 1.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#FAFAFA',
};

const mobileLabelSx = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const mobileValueSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

type Order = 'asc' | 'desc';

export const TransactionVolumeReport: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();

  // Filters state
  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);
  const [transactionType, setTransactionType] = useState('all');
  const [paymentMethod, setPaymentMethod] = useState('all');
  const [status, setStatus] = useState('all');
  const [siteId, setSiteId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [chargerId, setChargerId] = useState('');

  // Filter options state
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [chargers, setChargers] = useState<Array<{ id: string; name: string }>>([]);

  // Period type state (day/month)
  const [periodType, setPeriodType] = useState<'day' | 'month'>('day');

  // Tab state (0 = table, 1 = charts)
  const [tabValue, setTabValue] = useState(0);

  // Table sorting state
  const [orderBy, setOrderBy] = useState<keyof TimeSeriesData>('date');
  const [order, setOrder] = useState<Order>('desc');

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const tokenData = localStorage.getItem('ev_charging_auth_tokens_v2');
    const token = tokenData ? JSON.parse(tokenData).accessToken : null;

    if (!token) {
      console.error('No auth token found');
      return null;
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Fetch vendors on mount (vendors list is independent)
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        const response = await fetch(`${API_CONFIG.baseURL}/api/backend/reports/filters/vendors`, {
          headers,
        });
        const data = await response.json();
        setVendors(data.data || []);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    fetchVendors();
  }, []);

  // Fetch sites when vendor changes
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with vendor_id parameter if vendor is selected
        const url = vendorId
          ? `${API_CONFIG.baseURL}/api/backend/reports/filters/sites?vendor_id=${vendorId}`
          : `${API_CONFIG.baseURL}/api/backend/reports/filters/sites`;

        const response = await fetch(url, { headers });
        const data = await response.json();
        setSites(data.data || []);

        // Reset site selection if it's no longer in the filtered list
        if (siteId && !data.data?.some((s: any) => s.id === siteId)) {
          setSiteId('');
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };

    fetchSites();
  }, [vendorId]); // Re-fetch when vendor changes

  // Fetch chargers when vendor or site changes
  useEffect(() => {
    const fetchChargers = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with appropriate filter parameters
        let url = `${API_CONFIG.baseURL}/api/backend/reports/filters/chargers`;
        const params = new URLSearchParams();

        if (siteId) {
          params.append('site_id', siteId);
        } else if (vendorId) {
          params.append('vendor_id', vendorId);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await fetch(url, { headers });
        const data = await response.json();
        setChargers(data.data || []);

        // Reset charger selection if it's no longer in the filtered list
        if (chargerId && !data.data?.some((c: any) => c.id === chargerId)) {
          setChargerId('');
        }
      } catch (error) {
        console.error('Error fetching chargers:', error);
      }
    };

    fetchChargers();
  }, [vendorId, siteId]); // Re-fetch when vendor or site changes

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePeriodTypeChange = (event: React.MouseEvent<HTMLElement>, newPeriodType: 'day' | 'month' | null) => {
    if (newPeriodType !== null) {
      setPeriodType(newPeriodType);
    }
  };

  const handleRequestSort = (property: keyof TimeSeriesData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Fetch data
  const { data, loading, error, refetch } = useReportData<TransactionReportData>({
    endpoint: '/api/backend/reports/transactions',
    filters: {
      start_date: startDate,
      end_date: endDate,
      transaction_type: transactionType,
      payment_method: paymentMethod,
      status: status,
      group_by: periodType,
      ...(siteId && { site_id: siteId }),
      ...(vendorId && { vendor_id: vendorId }),
      ...(chargerId && { charger_id: chargerId }),
    },
  });

  // Export functionality
  const { exportToCSV, exportToExcel, exporting } = useReportExport();

  const handleExportCSV = () => {
    // Surface "no data yet" via toast rather than silently no-op'ing — the
    // user clicked Export and deserves feedback. The hook will fire its own
    // success/empty/failure toast based on the rows passed in.
    if (!data?.time_series || data.time_series.length === 0) {
      notify(
        translate('reports.export.noData', { fallback: 'No data to export — adjust filters and try again.' }),
        { type: 'warning' },
      );
      return;
    }

    exportToCSV(
      data.time_series,
      'transaction-volume',
      [
        { key: 'date', label: 'Date', format: 'date' },
        { key: 'count', label: 'Count', format: 'number' },
        { key: 'amount', label: 'Amount', format: 'currency' },
        { key: 'completed', label: 'Completed', format: 'number' },
        { key: 'success_rate', label: 'Success Rate %', format: 'number' },
      ]
    );
  };

  const handleExportExcel = async () => {
    if (!data || !data.time_series || data.time_series.length === 0) {
      notify(
        translate('reports.export.noData', { fallback: 'No data to export — adjust filters and try again.' }),
        { type: 'warning' },
      );
      return;
    }

    // Build filters object
    const filters: any = {
      startDate,
      endDate,
    };

    if (siteId) {
      const selectedSite = sites.find(s => s.id === siteId);
      if (selectedSite) {
        filters.site = { id: siteId, name: selectedSite.name };
      }
    }

    if (vendorId) {
      const selectedVendor = vendors.find(v => v.id === vendorId);
      if (selectedVendor) {
        filters.vendor = { id: vendorId, name: selectedVendor.name };
      }
    }

    if (chargerId) {
      const selectedCharger = chargers.find(c => c.id === chargerId);
      if (selectedCharger) {
        filters.charger = { id: chargerId, name: selectedCharger.name };
      }
    }

    await exportToExcel(
      data.time_series,
      'transaction-volume',
      {
        columns: [
          { key: 'date', label: translate('reports.table.date', { fallback: 'Date' }), format: 'date' },
          { key: 'count', label: translate('reports.table.transactionCount', { fallback: 'Count' }), format: 'number' },
          { key: 'amount', label: translate('reports.table.amount', { fallback: 'Amount' }), format: 'currency' },
          { key: 'completed', label: translate('reports.table.completed', { fallback: 'Completed' }), format: 'number' },
          { key: 'success_rate', label: translate('reports.table.successRate', { fallback: 'Success Rate %' }), format: 'number' },
        ],
        summary: {
          title: translate('reports.transaction.title', { fallback: 'Transaction Volume Analysis' }),
          data: [
            {
              label: translate('reports.kpi.totalTransactions', { fallback: 'Total Transactions' }),
              value: formatNumber(data.summary.total_count, 0)
            },
            {
              label: translate('reports.kpi.totalAmount', { fallback: 'Total Amount' }),
              value: formatCurrency(data.summary.total_amount)
            },
            {
              label: translate('reports.kpi.completedCount', { fallback: 'Completed' }),
              value: formatNumber(data.summary.completed_count, 0)
            },
            {
              label: translate('reports.kpi.successRate', { fallback: 'Success Rate' }),
              value: formatNumber(data.summary.success_rate, 1) + '%'
            },
            {
              label: translate('reports.kpi.avgTransactionAmount', { fallback: 'Avg Transaction Amount' }),
              value: formatCurrency(data.summary.avg_transaction_amount)
            },
          ]
        },
        sheetName: translate('reports.tabs.dataTable', { fallback: 'Data' }),
        includeTimestamp: true,
        translate: translate,
        filters: filters
      }
    );
  };

  // Format type names
  const formatTypeName = (type: string) => {
    const typeNames: Record<string, string> = {
      topup: translate('reports.transactionTypes.topup', { fallback: 'Top Up' }),
      payment: translate('reports.transactionTypes.payment', { fallback: 'Payment' }),
      refund: translate('reports.transactionTypes.refund', { fallback: 'Refund' }),
      credit: translate('reports.transactionTypes.credit', { fallback: 'Credit' }),
    };
    return typeNames[type] || type;
  };

  // Format payment method names
  const formatMethodName = (method: string) => {
    const methodNames: Record<string, string> = {
      wallet: translate('reports.paymentMethods.wallet', { fallback: 'Wallet' }),
      credit_card: translate('reports.paymentMethods.credit_card', { fallback: 'Credit Card' }),
      rfid_billing: translate('reports.paymentMethods.rfid_billing', { fallback: 'RFID Billing' }),
    };
    return methodNames[method] || method;
  };

  // Prepare pie chart data for payment methods
  const methodChartData = useMemo(() => {
    if (!data?.breakdown_by_method) return [];
    return data.breakdown_by_method.map((item) => ({
      name: formatMethodName(item.method),
      value: item.count,
      amount: item.amount,
    }));
  }, [data?.breakdown_by_method]);

  // Group data by month if periodType is 'month'
  const processedTimeSeriesData = useMemo(() => {
    if (!data?.time_series) return [];

    if (periodType === 'day') {
      return data.time_series;
    }

    // Group by month
    const monthlyData = new Map<string, TimeSeriesData>();

    data.time_series.forEach((item) => {
      const date = new Date(item.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (monthlyData.has(monthKey)) {
        const existing = monthlyData.get(monthKey)!;
        existing.count += item.count;
        existing.amount += item.amount;
        existing.completed += item.completed;
        // Recalculate success rate
        existing.success_rate = existing.count > 0 ? (existing.completed / existing.count) * 100 : 0;
      } else {
        monthlyData.set(monthKey, {
          date: monthKey,
          count: item.count,
          amount: item.amount,
          completed: item.completed,
          success_rate: item.success_rate,
        });
      }
    });

    return Array.from(monthlyData.values());
  }, [data?.time_series, periodType]);

  // Prepare sorted table data
  const sortedTableData = useMemo(() => {
    if (!processedTimeSeriesData.length) return [];

    const comparator = (a: TimeSeriesData, b: TimeSeriesData) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (orderBy === 'date') {
        return order === 'asc'
          ? new Date(aValue as string).getTime() - new Date(bValue as string).getTime()
          : new Date(bValue as string).getTime() - new Date(aValue as string).getTime();
      }

      return order === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    };

    return [...processedTimeSeriesData].sort(comparator);
  }, [processedTimeSeriesData, order, orderBy]);

  const topTypeChartData = useMemo(() => {
    if (!data?.breakdown_by_type) return [];
    return isMobile ? data.breakdown_by_type.slice(0, 5) : data.breakdown_by_type;
  }, [data?.breakdown_by_type, isMobile]);

  return (
    <ReportLayout
      title={translate('reports.transactions.title', { fallback: 'Transaction Volume' })}
      description={translate('reports.transactions.subtitle', { fallback: 'Transaction patterns and payment methods' })}
      filters={
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          siteId={siteId}
          onSiteIdChange={setSiteId}
          vendorId={vendorId}
          onVendorIdChange={setVendorId}
          chargerId={chargerId}
          onChargerIdChange={setChargerId}
          sites={sites}
          vendors={vendors}
          chargers={chargers}
          showSiteFilter={true}
          showVendorFilter={true}
          showChargerFilter={true}
          onRefresh={refetch}
          showGroupByFilter={false}
        />
      }
      exportButtons={{
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        exporting,
        disabled: !data || loading,
      }}
      loading={loading}
      error={error}
    >
      {data && (
        <Box sx={{ maxWidth: '100%', minWidth: 0 }}>
          {/* Tabs for Data Table vs Charts */}
          <Card sx={{ mb: 3, overflow: 'hidden', minWidth: 0 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, borderBottom: 1, borderColor: 'divider', px: { xs: 1.5, sm: 3 }, pt: 2, minWidth: 0 }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="report view tabs"
                  variant={isMobile ? "fullWidth" : "scrollable"}
                  scrollButtons={isMobile ? false : "auto"}
                  sx={{
                    minWidth: 0,
                    minHeight: { xs: 40, sm: 48 },
                    '& .MuiTab-root': {
                      minWidth: { xs: 0, sm: 90 },
                      minHeight: { xs: 40, sm: 48 },
                      px: { xs: 0.5, sm: 2 },
                      py: { xs: 0.75, sm: 1.5 },
                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                      textTransform: 'none',
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    },
                  }}
                >
                  <Tab
                    icon={<TableChart />}
                    iconPosition="start"
                    label={translate('reports.tabs.dataTable', { fallback: 'Data Table' })}
                  />
                  <Tab
                    icon={<ShowChart />}
                    iconPosition="start"
                    label={translate('reports.tabs.charts', { fallback: 'Charts' })}
                  />
                </Tabs>

                {/* Period Type Toggle */}
                <ToggleButtonGroup
                  value={periodType}
                  exclusive
                  onChange={handlePeriodTypeChange}
                  size="small"
                  aria-label="period type"
                  sx={{
                    mb: 1,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiToggleButton-root': {
                      flex: { xs: 1, sm: 'unset' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.75, sm: 0.5 },
                    },
                  }}
                >
                  <ToggleButton value="day" aria-label="daily view">
                    {translate('reports.groupBy.day', { fallback: 'Day' })}
                  </ToggleButton>
                  <ToggleButton value="month" aria-label="monthly view">
                    {translate('reports.groupBy.month', { fallback: 'Month' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Data Table Tab - First (index 0) */}
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#1A1A1A',
                      mb: 3,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {translate('reports.dailyBreakdown', { fallback: periodType === 'month' ? 'Monthly Breakdown' : 'Daily Breakdown' })}
                  </Typography>
                  {isMobile ? (
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Paper variant="outlined" sx={mobileRowCardSx}>
                        <Typography sx={{ ...mobileLabelSx, mb: 1 }}>
                          {translate('reports.table.total', { fallback: 'Total' })}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.transactionCount', { fallback: 'Count' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(data.summary.total_count)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.completed', { fallback: 'Completed' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(data.summary.completed_count)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.successRate', { fallback: 'Success Rate %' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(data.summary.success_rate, 1)}%</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.amount', { fallback: 'Amount' })}</Typography>
                            <Typography sx={{ ...mobileValueSx, color: '#009A75', fontWeight: 700 }}>
                              {formatCurrency(data.summary.total_amount)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>

                      {sortedTableData.map((row) => (
                        <Paper key={row.date} variant="outlined" sx={mobileRowCardSx}>
                          <Typography sx={{ ...mobileLabelSx, mb: 0.75 }}>
                            {periodType === 'month' ? translate('reports.table.date', { fallback: 'Month' }) : translate('reports.table.date', { fallback: 'Date' })}
                          </Typography>
                          <Typography sx={{ ...mobileValueSx, fontWeight: 700, mb: 1 }}>
                            {periodType === 'month' ? row.date : formatDate(row.date, 'long')}
                          </Typography>

                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.table.transactionCount', { fallback: 'Count' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(row.count)}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.table.completed', { fallback: 'Completed' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(row.completed)}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.table.successRate', { fallback: 'Success Rate %' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(row.success_rate, 1)}%</Typography>
                            </Box>
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.table.amount', { fallback: 'Amount' })}</Typography>
                              <Typography sx={{ ...mobileValueSx, color: '#009A75', fontWeight: 700 }}>
                                {formatCurrency(row.amount)}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0', maxHeight: 600 }}>
                        <Table stickyHeader sx={tableDetailsSx}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                              <TableCell>
                                <TableSortLabel
                                  active={orderBy === 'date'}
                                  direction={orderBy === 'date' ? order : 'asc'}
                                  onClick={() => handleRequestSort('date')}
                                >
                                  {translate('reports.table.date', { fallback: periodType === 'month' ? 'Month' : 'Date' })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'count'}
                                  direction={orderBy === 'count' ? order : 'asc'}
                                  onClick={() => handleRequestSort('count')}
                                >
                                  {translate('reports.table.transactionCount', { fallback: 'Count' })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'completed'}
                                  direction={orderBy === 'completed' ? order : 'asc'}
                                  onClick={() => handleRequestSort('completed')}
                                >
                                  {translate('reports.table.completed', { fallback: 'Completed' })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'success_rate'}
                                  direction={orderBy === 'success_rate' ? order : 'asc'}
                                  onClick={() => handleRequestSort('success_rate')}
                                >
                                  {translate('reports.table.successRate', { fallback: 'Success Rate %' })}
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'amount'}
                                  direction={orderBy === 'amount' ? order : 'asc'}
                                  onClick={() => handleRequestSort('amount')}
                                >
                                  {translate('reports.table.amount', { fallback: 'Amount' })}
                                </TableSortLabel>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedTableData.map((row) => (
                              <TableRow key={row.date} hover>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {periodType === 'month' ? row.date : formatDate(row.date, 'long')}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{formatNumber(row.count)}</TableCell>
                                <TableCell align="right">{formatNumber(row.completed)}</TableCell>
                                <TableCell align="right">{formatNumber(row.success_rate, 1)}%</TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#009A75' }}>
                                    {formatCurrency(row.amount)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                          <TableFooter>
                            <TableRow sx={{
                              backgroundColor: '#F5F5F5',
                              fontWeight: 600,
                              '& td': {
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: '#F5F5F5',
                                borderTop: '2px solid #E0E0E0',
                                zIndex: 2,
                              }
                            }}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {translate('reports.table.total', { fallback: 'Total' })}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatNumber(data.summary.total_count)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatNumber(data.summary.completed_count)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatNumber(data.summary.success_rate, 1)}%
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#009A75' }}>
                                  {formatCurrency(data.summary.total_amount)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableFooter>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>
              </TabPanel>

              {/* Charts Tab - Second (index 1) */}
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 }, minWidth: 0 }}>
                  {/* Transaction Volume Over Time Chart */}
                  <Box sx={{ mb: 3, p: { xs: 2, sm: 3 }, backgroundColor: '#FAFAFA', borderRadius: 2, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.transactionVolumeOverTime', { fallback: 'Transaction Volume Over Time' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                      <LineChart data={processedTimeSeriesData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(date) => periodType === 'month' ? date : formatDate(date)}
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          stroke="#606060"
                          interval={isMobile ? 'preserveStartEnd' : 0}
                          minTickGap={isMobile ? 24 : 12}
                        />
                        <YAxis
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                          stroke="#606060"
                          tickFormatter={(value) => formatNumber(value, 0)}
                          width={isMobile ? 42 : 54}
                        />
                        <Tooltip
                          formatter={(value: number, name: string) => {
                            if (name === 'amount') return formatCurrency(value);
                            if (name === 'success_rate') return `${formatNumber(value, 1)}%`;
                            return formatNumber(value, 0);
                          }}
                          labelFormatter={(date) => periodType === 'month' ? date : formatDate(date, 'long')}
                          contentStyle={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E0E0E0',
                            borderRadius: 8,
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="#009A75"
                          strokeWidth={isMobile ? 2 : 3}
                          dot={{ r: isMobile ? 3 : 4, fill: '#009A75' }}
                          name="Count"
                        />
                        <Line
                          type="monotone"
                          dataKey="success_rate"
                          stroke="#2196F3"
                          strokeWidth={isMobile ? 1.75 : 2}
                          dot={{ r: isMobile ? 2.5 : 3, fill: '#2196F3' }}
                          name="Success Rate %"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3, minWidth: 0 }}>
                    {/* Transaction by Type Chart */}
                    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#FAFAFA', borderRadius: 2, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#1A1A1A',
                          mb: 3,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {translate('reports.charts.transactionsByType', { fallback: 'Transactions by Type' })}
                      </Typography>
                      {topTypeChartData && topTypeChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={isMobile ? 300 : 300}>
                          {isMobile ? (
                            <BarChart
                              data={topTypeChartData}
                              layout="vertical"
                              margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                              <XAxis
                                type="number"
                                tick={{ fontSize: 10 }}
                                stroke="#606060"
                                tickFormatter={(value) => formatNumber(value, 0)}
                              />
                              <YAxis
                                type="category"
                                dataKey="type"
                                width={90}
                                tick={{ fontSize: 10 }}
                                stroke="#606060"
                                tickFormatter={(type) => String(formatTypeName(type)).slice(0, 14)}
                              />
                              <Tooltip
                                formatter={(value: number, name: string) => {
                                  if (name === 'amount') return formatCurrency(value);
                                  return formatNumber(value, 0);
                                }}
                                labelFormatter={(value) => formatTypeName(String(value))}
                                contentStyle={{
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E0E0E0',
                                  borderRadius: 8,
                                }}
                              />
                              <Bar dataKey="count" fill="#009A75" name="Count" radius={[0, 6, 6, 0]} />
                            </BarChart>
                          ) : (
                            <BarChart data={topTypeChartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                              <XAxis
                                dataKey="type"
                                tick={{ fontSize: 12 }}
                                stroke="#606060"
                                tickFormatter={(type) => formatTypeName(type)}
                                interval={0}
                              />
                              <YAxis
                                tick={{ fontSize: 12 }}
                                stroke="#606060"
                                tickFormatter={(value) => formatNumber(value, 0)}
                              />
                              <Tooltip
                                formatter={(value: number, name: string) => {
                                  if (name === 'amount') return formatCurrency(value);
                                  return formatNumber(value, 0);
                                }}
                                labelFormatter={(value) => formatTypeName(String(value))}
                                contentStyle={{
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E0E0E0',
                                  borderRadius: 8,
                                }}
                              />
                              <Bar dataKey="count" fill="#009A75" name="Count" radius={[8, 8, 0, 0]} />
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      ) : (
                        <Box sx={{ py: 8, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No transaction type data available
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Transaction by Payment Method Chart */}
                    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#FAFAFA', borderRadius: 2, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#1A1A1A',
                          mb: 3,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {translate('reports.charts.transactionsByMethod', { fallback: 'Transactions by Payment Method' })}
                      </Typography>
                      <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                        <PieChart>
                          <Pie
                            data={methodChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={isMobile ? false : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={isMobile ? 70 : 80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {methodChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number, name: string) => {
                              if (name === 'amount') return formatCurrency(value);
                              return formatNumber(value, 0);
                            }}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Legend
                            verticalAlign={isMobile ? 'bottom' : 'top'}
                            align="center"
                            wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Box>
              </TabPanel>
            </CardContent>
          </Card>
        </Box>
      )}
    </ReportLayout>
  );
};
